import { z } from "zod"
import {
  branchIdSchema,
  correlationIdSchema,
  entityIdSchema,
  legalEntityIdSchema,
} from "./identifiers.js"

const idempotencyKey = z
  .string()
  .regex(/^[A-Za-z0-9][A-Za-z0-9._:-]{0,127}$/)
const reasonCode = z.string().regex(/^[a-z][a-z0-9_]{1,63}$/)

const base = {
  requestVersion: z.literal(1),
  correlationId: correlationIdSchema,
  idempotencyKey,
  transferId: entityIdSchema,
  legalEntityId: legalEntityIdSchema,
  branchId: branchIdSchema,
}

export const inventoryTransferRequestV1Schema = z
  .object({
    ...base,
    expectedVersion: z.literal(0),
    revisionId: entityIdSchema,
    sourceLocationId: entityIdSchema,
    destinationLocationId: entityIdSchema,
    transitLocationId: entityIdSchema,
    itemMasterId: entityIdSchema,
    quantityMilli: z.number().int().positive().max(Number.MAX_SAFE_INTEGER),
    requestedForDate: z.string().date(),
    reasonCode,
  })
  .strict()
  .refine(
    ({ sourceLocationId, destinationLocationId, transitLocationId }) =>
      new Set([sourceLocationId, destinationLocationId, transitLocationId])
        .size === 3,
    { message: "Source, destination, and transit locations must differ." },
  )

export const inventoryTransferTransitionV1Schema = z
  .object({
    ...base,
    expectedVersion: z.number().int().positive(),
    revisionId: entityIdSchema,
    action: z.enum(["dispatch", "receive", "cancel", "exception"]),
    postingDate: z.string().date().nullable(),
    reasonCode,
    exceptionCode: z
      .enum([
        "quantity_discrepancy",
        "damage",
        "custody_lost",
        "destination_unavailable",
      ])
      .nullable(),
  })
  .strict()
  .superRefine((value, context) => {
    const postsStock = value.action === "dispatch" || value.action === "receive"
    if (postsStock !== (value.postingDate !== null)) {
      context.addIssue({
        code: "custom",
        message: "Dispatch and receipt require a posting date.",
      })
    }
    if ((value.action === "exception") !== (value.exceptionCode !== null)) {
      context.addIssue({
        code: "custom",
        message: "Only exception transitions require an exception code.",
      })
    }
  })

export const inventoryTransferResultV1Schema = z
  .object({
    entityType: z.literal("inventory_transfer"),
    entityId: entityIdSchema,
    state: z.enum([
      "requested",
      "in_transit",
      "received",
      "canceled",
      "exception",
    ]),
    version: z.number().int().positive(),
    legalEntityId: legalEntityIdSchema,
    branchId: branchIdSchema,
    sourceLocationId: entityIdSchema,
    destinationLocationId: entityIdSchema,
    transitLocationId: entityIdSchema,
    itemMasterId: entityIdSchema,
    quantityMilli: z.number().int().positive(),
    custody: z.enum([
      "source",
      "in_transit",
      "destination",
      "source_canceled",
      "in_transit_exception",
    ]),
    dispatchStockEntry: z.string().min(1).max(140).nullable(),
    receiptStockEntry: z.string().min(1).max(140).nullable(),
    exceptionCode: z.string().min(1).max(64).nullable(),
  })
  .strict()

export type InventoryTransferRequestV1 = z.infer<
  typeof inventoryTransferRequestV1Schema
>
export type InventoryTransferTransitionV1 = z.infer<
  typeof inventoryTransferTransitionV1Schema
>
export type InventoryTransferResultV1 = z.infer<
  typeof inventoryTransferResultV1Schema
>
